import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import { Link } from 'react-router-dom';

export default function Profile() {
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    genre: '',
    journey_stage: '',
    wants_bunkmate: ''
  });

  // Sync form data with user context when entering edit mode
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        gender: user.gender || '',
        genre: user.genre || '',
        journey_stage: user.journey_stage || '',
        wants_bunkmate: user.wants_bunkmate != null ? String(user.wants_bunkmate) : ''
      });
    }
  }, [user, isEditing]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        wants_bunkmate: formData.wants_bunkmate === '1' ? 1 : (formData.wants_bunkmate === '0' ? 0 : null)
      };

      // Update backend
      await api.put('users_gwacamp', user.id, payload);
      
      // Update local AuthContext so changes reflect globally
      setUser({ ...user, ...payload });
      
      setIsEditing(false);
      showToast('Profile updated successfully.');
    } catch (error) {
      console.error("Failed to update profile", error);
      showToast('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 bg-forest-green text-white px-5 py-3 rounded-md shadow-lg z-50 animate-fadeIn font-bold">
          {toast}
        </div>
      )}

      <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md flex justify-between items-center">
        <h1 className="text-2xl font-bold text-dark-green flex items-center gap-3">
          <SafeIcon name="User" /> Camper Profile
        </h1>
        {!isEditing && (
          <Button variant="ghost" onClick={() => setIsEditing(true)} className="text-sm py-1.5 px-3 bg-white/50 hover:bg-white">
            <SafeIcon name="Edit2" className="mr-2 text-xs" /> Edit Profile
          </Button>
        )}
      </div>

      <Card className="space-y-6">
        <div className="flex items-center gap-4 border-b border-tan pb-6">
          <div className="w-16 h-16 bg-forest-green rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0">
            {user?.name?.charAt(0) || 'C'}
          </div>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <Input 
                value={formData.name} 
                onChange={e => setFormData({...formData, name: e.target.value})} 
                className="max-w-sm mb-1"
                placeholder="Your Full Name"
              />
            ) : (
              <h2 className="text-xl font-bold text-dark-green truncate">{user?.name}</h2>
            )}
            <p className="text-muted truncate">{user?.email}</p>
          </div>
          <div className="ml-auto shrink-0 hidden sm:block">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
              user?.pass_type === 'allaccess' ? 'bg-gwa-blue text-white' : 'bg-tan text-dark-green'
            }`}>
              {user?.pass_type === 'allaccess' ? 'All-Access' : 'Free Pass'}
            </span>
          </div>
        </div>

        {isEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-green">Gender</label>
              <select 
                className="px-4 py-2 rounded-md border border-amber bg-cream focus:ring-2 focus:ring-forest-green outline-none"
                value={formData.gender}
                onChange={e => setFormData({...formData, gender: e.target.value})}
              >
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-green">Genre</label>
              <select 
                className="px-4 py-2 rounded-md border border-amber bg-cream focus:ring-2 focus:ring-forest-green outline-none"
                value={formData.genre}
                onChange={e => setFormData({...formData, genre: e.target.value})}
              >
                <option value="">Select...</option>
                <option value="fiction">Fiction</option>
                <option value="nonfiction">Non-Fiction</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-dark-green">Journey Stage</label>
              <select 
                className="px-4 py-2 rounded-md border border-amber bg-cream focus:ring-2 focus:ring-forest-green outline-none"
                value={formData.journey_stage}
                onChange={e => setFormData({...formData, journey_stage: e.target.value})}
              >
                <option value="">Select...</option>
                <option value="idea">Idea / Outline</option>
                <option value="draft">Draft in Progress</option>
                <option value="rough">Rough Complete</option>
                <option value="ready">Ready to Refine (Editing)</option>
                <option value="published">Published</option>
              </select>
            </div>

            <div className="flex flex-col gap-1 border-l-4 border-amber pl-4 bg-amber/5 rounded-r-md py-2">
              <label className="text-sm font-medium text-dark-green flex items-center gap-1">
                Bunkmate Pairing <span className="text-orange-600">*</span>
              </label>
              <select 
                className="px-4 py-2 rounded-md border border-amber bg-white focus:ring-2 focus:ring-forest-green outline-none"
                value={formData.wants_bunkmate}
                onChange={e => setFormData({...formData, wants_bunkmate: e.target.value})}
              >
                <option value="">Select...</option>
                <option value="1">Yes, pair me with a bunkmate</option>
                <option value="0">No, I'll write solo</option>
              </select>
              <p className="text-xs text-muted italic mt-1">* This feature is reserved for All-Access Pass holders.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <label className="text-xs text-muted uppercase font-semibold">Gender</label>
              <p className="font-medium text-dark-green capitalize">{user?.gender || 'Not set'}</p>
            </div>
            <div>
              <label className="text-xs text-muted uppercase font-semibold">Genre</label>
              <p className="font-medium text-dark-green capitalize">{user?.genre || 'Not set'}</p>
            </div>
            <div>
              <label className="text-xs text-muted uppercase font-semibold">Journey Stage</label>
              <p className="font-medium text-dark-green capitalize">{user?.journey_stage?.replace('_', ' ') || 'Not set'}</p>
            </div>
            <div className="border-l-2 border-amber pl-4">
              <label className="text-xs text-muted uppercase font-semibold">Bunkmate Pairing</label>
              <p className="font-medium text-dark-green capitalize">
                {user?.wants_bunkmate == 1 ? 'Yes' : (user?.wants_bunkmate == 0 ? 'No' : 'Not set')}
              </p>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="flex gap-3 pt-6 border-t border-tan mt-6">
            <Button onClick={handleSave} disabled={saving} className="px-8">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        )}

        {!isEditing && (
          <div className="pt-6 border-t border-tan mt-6">
            <Link to="/cabin">
              <Button variant="outline" className="w-full sm:w-auto">
                <SafeIcon name="Download" /> View & Export Compass PDF
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}